import { ValidationErrors } from '@angular/forms';
import { WorkspaceDatabaseKeys } from '@database/workspace';
import { AppRoutingKeys, fileBooleanMappings } from '@shared';
import { firstValueFrom, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { IntervalTableEnum, ValidationKeys } from '../../models';
import { IntervalService } from '../../services/interval.service';
import { IAsyncValidatorHandler } from '../async-validator-handler.interface';
import { CrossTableParams } from '../models/cross-table-params-type';

export class IntervalRelatedCheck implements IAsyncValidatorHandler {
	private next: IAsyncValidatorHandler;

	public constructor(private intervalService: IntervalService) {}

	public setNext(nextHandler: IAsyncValidatorHandler) {
		this.next = nextHandler;
	}

	public async handle(crossTableParams: CrossTableParams): Promise<ValidationErrors | null> {
		const interValRelatedCheck: ValidationErrors | null = await firstValueFrom(
			of(
				fileBooleanMappings[crossTableParams.tableMetadata[WorkspaceDatabaseKeys.tables.isIntervalRelated]]
			).pipe(
				switchMap((isIntervalRelated: boolean) => {
					if (isIntervalRelated && !this.isMajorIntervalTable(crossTableParams)) {		
						return this.intervalService
							.isIntervalBreached$(
								this.getIntervalTable(crossTableParams),
								crossTableParams.queryParamsMap.get(AppRoutingKeys.queryParamsMap.workfileId),
								crossTableParams.queryParamsMap.get(AppRoutingKeys.queryParamsMap.projectNumber),
								crossTableParams.queryParamsMap.get(AppRoutingKeys.queryParamsMap.entityName),
								crossTableParams.queryParamsMap.get(AppRoutingKeys.queryParamsMap.entryId),
								crossTableParams.queryParamsMap.get(AppRoutingKeys.queryParamsMap.childEntryId),
								crossTableParams.crossControlKey === WorkspaceDatabaseKeys.columns.depthFrom
									? crossTableParams.crossControlValue
									: crossTableParams.controlValue,
								crossTableParams.crossControlKey === WorkspaceDatabaseKeys.columns.depthTo
									? crossTableParams.crossControlValue
									: crossTableParams.controlValue
							)
							.pipe(
								map((intervalFound: boolean) => {
									const appliedValidation = intervalFound
										? null
										: { [ValidationKeys.validationRules.intervalBreachRule]: true };

									if (!crossTableParams.crossControl.errors) {
										crossTableParams.crossControl.setErrors(appliedValidation);
									}

									return appliedValidation;
								})
							);
					}
					return of(null);
				})
			)
		);

		if (interValRelatedCheck) {
			return interValRelatedCheck;
		}

		if (this.next) {
			return await this.next.handle(crossTableParams);
		}

		return null;
	}

	private getIntervalTable(crossTableParams: CrossTableParams): IntervalTableEnum {
		if (
			crossTableParams.queryParamsMap.get(AppRoutingKeys.queryParamsMap.tableName) === IntervalTableEnum.HoleIntervalMinor) {
			return IntervalTableEnum.HoleIntervalMajor;
		}

		if (crossTableParams.isMinor !== undefined) {
			return crossTableParams.isMinor ? IntervalTableEnum.HoleIntervalMinor : IntervalTableEnum.HoleIntervalMajor;
		}
		return null;
	}

	private isMajorIntervalTable(crossTableParams: CrossTableParams): boolean {
		return (
			crossTableParams.queryParamsMap.get(AppRoutingKeys.queryParamsMap.tableName) ===
			IntervalTableEnum.HoleIntervalMajor
		);
	}
}
